import React, { useState } from 'react';
import firebaseAuthService from '../services/firebaseAuthService';
import { useTranslation } from '../services/translationService';
import { Field } from './ui';
import { AuthLayout, PasswordInput, authErrorText } from './AuthLayout';

const CANCELLED = ['auth/popup-closed-by-user', 'auth/cancelled-popup-request'];

const FirebaseFarmLogin = ({ onLoginSuccess, onRegisterRequest, authService = firebaseAuthService }) => {
  const { language } = useTranslation();
  const ro = language === 'ro';
  const [form, setForm] = useState({ email: '', password: '', keepLoggedIn: false });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const handleLogin = async (event) => {
    event.preventDefault();
    setError('');
    setNotice('');
    if (!form.email.trim() || !form.password.trim()) {
      setError(ro ? 'Completează emailul și parola.' : 'Please enter both email and password.');
      return;
    }
    setLoading(true);
    try {
      const { user, farm } = await authService.login(form.email.trim(), form.password.trim(), form.keepLoggedIn);
      onLoginSuccess({ user, farm });
    } catch (err) {
      setError(authErrorText(err, ro));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    setNotice('');
    setLoading(true);
    try {
      const { user, farm } = await authService.signInWithGoogle();
      onLoginSuccess({ user, farm });
    } catch (err) {
      if (!CANCELLED.includes(err?.code)) setError(authErrorText(err, ro));
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async () => {
    setError('');
    setNotice('');
    if (!form.email.trim()) {
      setError(ro ? 'Scrie emailul mai întâi, apoi apasă din nou.' : 'Enter your email first, then press this again.');
      return;
    }
    setLoading(true);
    try {
      await authService.resetPassword(form.email.trim());
      setNotice(ro ? 'Ți-am trimis un email cu linkul pentru parolă nouă. Verifică și folderul Spam.' : 'We sent you an email with a link for a new password. Check your spam folder too.');
    } catch (err) {
      setError(authErrorText(err, ro));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title={ro ? 'Ferma ta, într-un singur loc' : 'Your farm, in one place'}
      text={ro ? 'Câmpuri, parcele, arendă, stocuri și motorină, pe toate dispozitivele.' : 'Fields, parcels, rent, stock and fuel, on all your devices.'}>
      <div>
        <h2>{ro ? 'Conectare' : 'Sign in'}</h2>
        <p className="au-sub">{ro ? 'Intră în contul fermei tale' : 'Sign in to your farm account'}</p>
      </div>
      {error && <p className="pm-error" role="alert">{error}</p>}
      {notice && <p className="au-ok" role="status">{notice}</p>}
      <form className="au-form" onSubmit={handleLogin} noValidate>
        <Field label="Email">
          <input type="email" autoComplete="username" autoCapitalize="none" inputMode="email" value={form.email} disabled={loading}
            onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Field>
        <Field label={ro ? 'Parolă' : 'Password'}>
          <PasswordInput show={showPassword} onToggle={() => setShowPassword(!showPassword)} ro={ro} autoComplete="current-password" value={form.password} disabled={loading}
            onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </Field>
        <div className="au-line">
          <label className="au-check">
            <input type="checkbox" checked={form.keepLoggedIn} disabled={loading} onChange={(e) => setForm({ ...form, keepLoggedIn: e.target.checked })} />
            {ro ? 'Ține-mă conectat' : 'Keep me signed in'}
          </label>
          <button type="button" className="au-link" disabled={loading} onClick={handleForgot}>{ro ? 'Ai uitat parola?' : 'Forgot password?'}</button>
        </div>
        <button type="submit" className="pm-btn-primary" disabled={loading}>{loading ? (ro ? 'Se conectează…' : 'Signing in…') : (ro ? 'Conectează-te' : 'Sign in')}</button>
      </form>
      <div className="au-divider">{ro ? 'sau' : 'or'}</div>
      <button type="button" className="au-google" disabled={loading} onClick={handleGoogle}>
        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        {ro ? 'Continuă cu Google' : 'Continue with Google'}
      </button>
      <p className="au-foot">
        {ro ? 'Ai primit un cod de invitație? ' : 'Got an invitation code? '}
        <button type="button" className="au-link" disabled={loading} onClick={onRegisterRequest}>{ro ? 'Înregistrează ferma' : 'Register your farm'}</button>
      </p>
    </AuthLayout>
  );
};

export default FirebaseFarmLogin;
